import React, { useEffect } from 'react';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import { TGetTransactionListingFilters } from '~types/feat/gold';
import { initialState as approvalInitialState } from '~redux/slices/approval';

import SearchAndFilterBar, { TSearchAndFilterBarProps } from '~components/molecules/SearchAndFilterBar';

import { GoldTypeEnum, MetalTypeEnum, SilverTypeEnum, TransactionActionEnum } from '~utils/enums';

import * as Styles from './_goldTransactionApprovalSearchAndFilterBar.module.scss';
import dayjs from 'dayjs';

const GoldTransactionApprovalSearchAndFilterBar = () => {
    const dispatch = useAppDispatch();
    const filters = useAppSelector(state => state.approval.pendingTransactionListing.filters);
    const branchesList = useAppSelector(state => state.branch.allBranches);
    const { allBranchAccess, branchCode } = useAppSelector(state => state.auth.userDetails);

    const initialFilters = approvalInitialState.pendingTransactionListing.filters;

    const setFilters = (inputFilters: TGetTransactionListingFilters) => dispatch(combinedActions.approvalSetPendingTransactionListingFilters(inputFilters));
    const handleSearchClick = () => dispatch(combinedActions.approvalGetPendingTransactionListingAttempt());
    const handleCancelClick = () => {
        setFilters({
            ...initialFilters,
            branchName: !allBranchAccess
                ? branchesList.find(
                    (branch) => branch.branchCode === branchCode,
                )?.branchName || ''
                : '',
            goldType: '',
            silverType: '',
        });

        handleSearchClick();
    };

    useEffect(() => {
        if (branchesList.length === 0) return;

        const defaultBranch =
            !allBranchAccess
                ? branchesList.find(b => b.branchCode === branchCode)
                : branchesList[0];

        if (!defaultBranch) return;

        setFilters({
            ...filters,
            branchName: defaultBranch.branchName,
            branchCode: defaultBranch.branchCode,
        });
    }, [branchesList]);

    const massagedBranchesListOption = () => {
        if (!branchesList.length) return [];

        return branchesList.map((branch) => ({
            label: branch.branchName,
            value: branch.branchCode,
        }));
    };

    const isSilver = filters.metalType === MetalTypeEnum[MetalTypeEnum.Silver];

    const fields: TSearchAndFilterBarProps['fields'] = [
        {
            label: 'Customer Name:',
            type: 'input' as const,
            inputParams: {
                value: filters.name,
                setValue: (event) => setFilters({ ...filters, name: event.target.value }),
                onKeyDown: (event) => { if (event.key === 'Enter') handleSearchClick(); },
                className: Styles.input,
            },
        },
        {
            label: 'ID No.:',
            type: 'input' as const,
            inputParams: {
                value: filters.idNo,
                setValue: (event) => setFilters({ ...filters, idNo: event.target.value }),
                onKeyDown: (event) => { if (event.key === 'Enter') handleSearchClick(); },
                className: Styles.input,
            },
        },
        {
            label: 'Branch:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: massagedBranchesListOption(),
                value: filters.branchName || '',
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => setFilters({
                    ...filters,
                    branchName: event.target.value,
                }),
                optionSelected: !!branchesList.find((branch) => branch.branchName === filters.branchName),
                noOptionsMessage: 'No branches found',
                className: Styles.input,
                disabled: !allBranchAccess,
            },
        },
        {
            label: 'Transaction Type:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: [
                    {
                        label: TransactionActionEnum[TransactionActionEnum['Bank Sell']],
                        value: TransactionActionEnum['Bank Sell'],
                    },
                    {
                        label: TransactionActionEnum[TransactionActionEnum['Bank Buy']],
                        value: TransactionActionEnum['Bank Buy'],
                    },
                ],
                value: filters.action,
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, action: event.target.value }),
                optionSelected: !!TransactionActionEnum[filters.action as keyof typeof TransactionActionEnum],
                noOptionsMessage: 'No transaction type found',
                className: Styles.input,
            },
        },
        {
            label: 'Date From:',
            type: 'datepicker' as const,
            datepickerParams: {
                date: filters.dateFrom,
                setDate: (date: string) => {
                    if (date > filters?.dateTo) {
                        setFilters({ ...filters, dateFrom: date, dateTo: '' });
                    } else setFilters({ ...filters, dateFrom: date });
                },
                startDate: filters.dateFrom,
                endDate: filters.dateTo,
                selectsStart: true,
                maxDate: dayjs().toISOString(),
            },
        },
        {
            label: 'Date To:',
            type: 'datepicker' as const,
            datepickerParams: {
                date: filters.dateTo,
                minDate: filters.dateFrom,
                setDate: (date: string) => setFilters({ ...filters, dateTo: date }),
                startDate: filters.dateFrom,
                endDate: filters.dateTo,
                selectsEnd: true,
            },
        },
        {
            label: 'Metal Type:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: [
                    {
                        label: MetalTypeEnum[MetalTypeEnum.Gold],
                        value: MetalTypeEnum[MetalTypeEnum.Gold],
                    },
                    {
                        label: MetalTypeEnum[MetalTypeEnum.Silver],
                        value: MetalTypeEnum[MetalTypeEnum.Silver],
                    },
                ],
                value: filters.metalType,
                setValue: (event: React.ChangeEvent<HTMLInputElement>) =>
                    setFilters({
                        ...filters,
                        metalType: event.target.value,
                        goldType: '',
                        silverType: '',
                    }),
                optionSelected: !!filters.metalType,
                noOptionsMessage: 'No metal type found',
            },
        },
        {
            label: 'Physical / Paper:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: isSilver
                    ? [
                        {
                            label: SilverTypeEnum[SilverTypeEnum.PhysicalSilver]
                                .split(/(?=[A-Z])/)
                                .join(' '),
                            value: SilverTypeEnum[SilverTypeEnum.PhysicalSilver],
                        },
                        {
                            label: SilverTypeEnum[SilverTypeEnum.PaperSilver]
                                .split(/(?=[A-Z])/)
                                .join(' '),
                            value: SilverTypeEnum[SilverTypeEnum.PaperSilver],
                        },
                    ]
                    : [
                        {
                            label: GoldTypeEnum[GoldTypeEnum.PhysicalGold]
                                .split(/(?=[A-Z])/)
                                .join(' '),
                            value: GoldTypeEnum[GoldTypeEnum.PhysicalGold],
                        },
                        {
                            label: GoldTypeEnum[GoldTypeEnum.PaperGold]
                                .split(/(?=[A-Z])/)
                                .join(' '),
                            value: GoldTypeEnum[GoldTypeEnum.PaperGold],
                        },
                    ],

                value: isSilver
                    ? String(filters.silverType || '')
                    : String(filters.goldType || ''),

                setValue: (event: React.ChangeEvent<HTMLInputElement>) => {
                    const value = event.target.value;

                    setFilters({
                        ...filters,
                        goldType: isSilver ? '' : value,
                        silverType: isSilver ? value : '',
                    });
                },

                optionSelected: isSilver
                    ? !!filters.silverType
                    : !!filters.goldType,

                noOptionsMessage: 'No type found',
            },
        },
    ];

    return (
        <SearchAndFilterBar
            fields={fields}
            searchOnClick={handleSearchClick}
            cancelOnClick={handleCancelClick}
            containerClassName={Styles['content-container']}
        />
    );
};

export default GoldTransactionApprovalSearchAndFilterBar;
